import { motion } from 'framer-motion'
import { Link } from 'react-router'
import { fadeRise } from './motion'

interface Props {
  /** The uppercase label. Omit for a block that needs rhythm but no heading. */
  title?: string
  /** Optional "See all" style link on the right of the heading. */
  action?: { label: string; to: string }
  /** A sentence under the heading, for a section that needs explaining. */
  description?: string
  children: React.ReactNode
  className?: string
}

/**
 * One block of a page: an eyebrow heading, an optional link, and the content.
 *
 * Exists so vertical rhythm is decided once. Pages used to set their own
 * `space-y-4` / `gap-3` / `mb-6`, which is why no two screens lined up.
 */
export function Section({ title, action, description, children, className = '' }: Props) {
  return (
    <motion.section variants={fadeRise} className={className}>
      {(title || action) && (
        <div className="mb-3 px-1">
          <div className="flex items-baseline justify-between gap-3">
            {title && <h2 className="eyebrow text-foreground/40">{title}</h2>}
            {action && (
              <Link
                to={action.to}
                className="text-xs font-medium text-primary transition-opacity hover:opacity-70"
              >
                {action.label}
              </Link>
            )}
          </div>
          {description && (
            <p className="mt-1.5 text-xs leading-relaxed text-foreground/45">{description}</p>
          )}
        </div>
      )}
      {children}
    </motion.section>
  )
}
